import { parseProject, type Project } from "./schema";
import { applyResourceSnapshot, parseResource, type Resource } from "./catalog";
import {
  reusableFormSchema,
  suggestFormMapping,
  formFieldCompatible,
} from "./library";

/** Root shared forms already own reusable collections; retain their existing collection identities. */
export function isGlobalProfileCollection(resource: Resource) {
  return (
    resource.kind === "form" &&
    !resource.projectId &&
    !reusableFormSchema.parse(resource.value).profileSource
  );
}
export function assignGlobalProfileCollection(
  project: Project,
  resource: Resource,
  mapping?: Record<string, string>,
): Project {
  const row = parseResource(resource);
  if (
    !isGlobalProfileCollection(row) ||
    resource.archived ||
    row.tenantId !== project.tenantId
  )
    throw new Error(
      "Select an available global profile collection in this workspace",
    );
  const definition = reusableFormSchema.parse(row.value);
  const bindings =
    mapping ?? suggestFormMapping(definition.fields, project.fields).mapping;
  if (
    !Object.entries(bindings).every(([inputId, targetId]) => {
      const input = definition.fields.find((f) => f.id === inputId),
        target = project.fields.find((f) => f.id === targetId);
      return input && target && formFieldCompatible(input, target);
    }) ||
    new Set(Object.values(bindings)).size !== Object.keys(bindings).length
  )
    throw new Error(
      "Map each profile input to a unique compatible project variable",
    );
  const existing = project.formInstances.find(
    (instance) =>
      instance.profileOnly &&
      project.forms.find((form) => form.id === instance.formId)?.profileLibrary
        ?.id === row.id,
  );
  const base = existing
    ? removeGlobalProfileAssignment(project, existing.id)
    : project;
  const next = applyResourceSnapshot(base, row, { formApplication: "mapped" });
  const instance = next.formInstances.at(-1)!,
    form = next.forms.at(-1)!;
  instance.profileOnly = true;
  delete instance.mappedOnly;
  instance.mapping = { ...bindings };
  if (existing) {
    if (!base.forms.some((form) => form.id === existing.formId))
      form.id = existing.formId;
    instance.formId = form.id;
    instance.id = existing.id;
  }
  return parseProject(next);
}
export function removeGlobalProfileAssignment(
  project: Project,
  instanceId: string,
): Project {
  const instance = project.formInstances.find(
    (instance) => instance.id === instanceId && instance.profileOnly,
  );
  if (!instance) throw new Error("Global profile assignment not found");
  const formInstances = project.formInstances.filter(
    (item) => item.id !== instanceId,
  );
  return parseProject({
    ...project,
    formInstances,
    forms: formInstances.some((item) => item.formId === instance.formId)
      ? project.forms
      : project.forms.filter((form) => form.id !== instance.formId),
  });
}
